import { ticketType } from "@agentcall/core/type"
import { useState } from "react"
import { operationRuleApi } from "@/api/client/operation-rule"
import type {
  TicketUpdateContractorBodyContractorStatus,
  TicketUpdateContractorBodyContractorType,
} from "@/api/client/ticket"
import { Button } from "@/components/ui/button"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { FormRequiredLabel } from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea/textarea"

interface ContractorInfoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultValues?: {
    contractorStatus?: TicketUpdateContractorBodyContractorStatus
    contractorType?: TicketUpdateContractorBodyContractorType
    contractorName?: string
    contractorContact?: string
    contractorContactInfo?: string
    contractorScheduledDate?: Date
    contractorNote?: string
  }
  propertyId?: number | null
  onSave?: (data: ContractorInfoData) => void
  loading?: boolean
}

export interface ContractorInfoData {
  contractorStatus: TicketUpdateContractorBodyContractorStatus
  contractorType: TicketUpdateContractorBodyContractorType
  contractorName: string
  contractorContact: string
  contractorContactInfo: string
  contractorScheduledDate: Date | undefined
  contractorNote: string
}

const statusOptions = ticketType.CONTRACTOR_STATUSES.map((status) => ({
  value: status.key,
  label: status.value,
}))

const assignmentTypeOptions = ticketType.CONTRACTOR_TYPES.map((type) => ({
  value: type.key,
  label: type.value,
}))

const DEFAULT_CONTRACTOR_NOTE = `メール：
FAX：
上記以外の連絡先電話番号：
営業時間：
定休日：
その他：`

export function ContractorInfoDialog({
  open,
  onOpenChange,
  defaultValues = {},
  propertyId,
  onSave,
  loading = false,
}: ContractorInfoDialogProps) {
  const [formData, setFormData] = useState<ContractorInfoData>({
    contractorStatus: defaultValues.contractorStatus ?? null,
    contractorType: defaultValues.contractorType ?? null,
    contractorName: defaultValues.contractorName || "",
    contractorContact: defaultValues.contractorContact || "",
    contractorContactInfo: defaultValues.contractorContactInfo || "",
    contractorScheduledDate: defaultValues.contractorScheduledDate || undefined,
    contractorNote: defaultValues.contractorNote || DEFAULT_CONTRACTOR_NOTE,
  })
  const [selectedDesignatedId, setSelectedDesignatedId] = useState("")

  const isContractorStatusSelected = Boolean(formData.contractorStatus)
  const isOwnerSpecified = formData.contractorType === ticketType.CONTRACTOR_TYPE_OWNER_SPECIFIED
  const { data: ruleListData } = operationRuleApi.useOperationRuleList(undefined, {
    query: {
      enabled: open && isOwnerSpecified && propertyId !== undefined,
    },
  })
  // この物件に適用されるルール(物件固有 → 無ければオーナー既定)の指定業者を出す(E-13)
  const appliedRule =
    ruleListData?.propertyRules.find((rule) => rule.propertyId === propertyId)?.operationRule ??
    ruleListData?.defaultRule
  const designatedContractors = appliedRule?.designatedContractors ?? []

  const handleSelectDesignatedContractor = (value: string) => {
    setSelectedDesignatedId(value)
    // 業者はルール内の配列順で識別する
    const contractor = designatedContractors[Number(value)]
    if (!contractor) return

    setFormData((current) => ({
      ...current,
      contractorName: contractor.name,
      contractorContact: contractor.contactPerson || "",
      contractorContactInfo: contractor.contactInfo,
    }))
  }

  const handleSave = async () => {
    await onSave?.(formData)
    onOpenChange(false)
  }

  const isFormValid =
    formData.contractorStatus && formData.contractorType && formData.contractorName

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[345px] p-6 rounded-lg">
        <DialogHeader className="text-center">
          <DialogTitle className="text-base font-semibold text-slate-900 text-center">
            業者依頼情報を変更します
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="contractorStatus" className="text-sm font-semibold text-slate-600">
              業者対応状況
            </Label>
            <Select
              value={formData.contractorStatus || ""}
              onValueChange={(value) =>
                setFormData({
                  ...formData,
                  contractorStatus: value as TicketUpdateContractorBodyContractorStatus,
                })
              }
            >
              <SelectTrigger className="h-10 text-sm">
                <SelectValue placeholder="選択してください" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label
              htmlFor="contractorType"
              className="flex items-center gap-1 text-sm font-semibold text-slate-600"
            >
              手配先業者
              {isContractorStatusSelected && <FormRequiredLabel />}
            </Label>
            <Select
              value={formData.contractorType || ""}
              onValueChange={(value) =>
                setFormData({
                  ...formData,
                  contractorType: value as TicketUpdateContractorBodyContractorType,
                })
              }
            >
              <SelectTrigger className="h-10 text-sm">
                <SelectValue placeholder="選択してください" />
              </SelectTrigger>
              <SelectContent>
                {assignmentTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isOwnerSpecified && (
            <div className="space-y-1">
              <Label
                htmlFor="designatedContractor"
                className="text-sm font-semibold text-slate-600"
              >
                指定業者から選択
              </Label>
              <Select
                value={selectedDesignatedId || ""}
                onValueChange={handleSelectDesignatedContractor}
              >
                <SelectTrigger className="h-10 text-sm">
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  {designatedContractors.map((contractor, index) => (
                    <SelectItem key={`${index}-${contractor.name}`} value={String(index)}>
                      {contractor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {designatedContractors.length === 0 && (
                <p className="text-xs text-slate-400">登録されている指定業者はありません</p>
              )}
            </div>
          )}

          <div className="space-y-1">
            <Label
              htmlFor="contractorName"
              className="flex items-center gap-1 text-sm font-semibold text-slate-600"
            >
              業者名
              {isContractorStatusSelected && <FormRequiredLabel />}
            </Label>
            <Input
              id="contractorName"
              value={formData.contractorName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormData({ ...formData, contractorName: e.target.value })
              }
              placeholder="○○工務店"
              className="h-10 text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="contractorContact" className="text-sm font-semibold text-slate-600">
              業者担当者
            </Label>
            <Input
              id="contractorContact"
              value={formData.contractorContact}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormData({ ...formData, contractorContact: e.target.value })
              }
              placeholder="田中 太郎"
              className="h-10 text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label
              htmlFor="contractorContactInfo"
              className="flex items-center gap-1 text-sm font-semibold text-slate-600"
            >
              業者連絡先（電話）
              {isContractorStatusSelected && <FormRequiredLabel />}
            </Label>
            <Input
              id="contractorContactInfo"
              value={formData.contractorContactInfo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormData({ ...formData, contractorContactInfo: e.target.value })
              }
              placeholder="03-1234-5678"
              className="h-10 text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label
              htmlFor="contractorScheduledDate"
              className="text-sm font-semibold text-slate-600"
            >
              業者対応予定日
            </Label>
            <DatePicker
              id="contractorScheduledDate"
              value={formData.contractorScheduledDate}
              onChange={(date) => setFormData({ ...formData, contractorScheduledDate: date })}
              placeholder="2025-01-01"
              className="text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="contractorNote" className="text-sm font-semibold text-slate-600">
              備考
            </Label>
            <Textarea
              id="contractorNote"
              value={formData.contractorNote}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFormData({ ...formData, contractorNote: e.target.value })
              }
              className="min-h-[80px] text-sm resize-none"
              placeholder="業者から対応完了報告をいただいたので、完了とします。"
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
            キャンセル
          </Button>
          <Button onClick={handleSave} disabled={!isFormValid} loading={loading} className="w-full">
            変更する
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
